import { HEADER } from '.';

export type CreatedTrait = {
  creator: string;
  creationDate: string;
  applicationCreator: string;
};

export type CreatedAndModifiedTrait = {
  lastModifier: string;
  lastModificationDate: string;
  lastApplicationModifier: string;
} & CreatedTrait;

export type BaseContent = {
  mimeType: string;
  size: number;
  uploadDate: string;
  fileName: string;
  creator: string;
  ocrRequest?: any;
};

export type Page = {
  page: number;
  pageSize: number;
  totalCount: number;
};

export type SearchPage = Page & {
  resultsCount: number;
};

export type AuditBase = {
  documentationVersion: '1.0.0';
  documentation: string;
  userId: string;
  clientId: string;
  occurredOn: string;
  comment: string;
};

export type ModelExtendedExperience = {
  keyMetadataPreferenceId: string | undefined;
};

// Kept for backward-compat with any code still referencing these types directly.
export type BaseUrl = {
  baseUrl: string;
};

export type AuthParam = {
  authorizationHeader: string | null;
};

export type DocumentParameters = { workspaceId: string; documentId: string };

export type DocumentMetadataParameters = DocumentParameters & {
  confidentialityLevel?: string;
  destroyDate?: string;
  contentsOutsideSGNetwork?: boolean;
};

export type PageParameters = {
  page?: string;
  size?: string;
};

// ---------------------------------------------------------------------------
// Response handlers (used internally by FetchClient)
// ---------------------------------------------------------------------------

const _computeBasicResponse = (response: Response): Promise<any> => {
  if (response.ok || response.status === 401) {
    return response.json();
  } else if (response.status === 500) {
    throw response.json().then((errorJson) => {
      return { ...errorJson, traceId: response.headers.get('trace-id') };
    });
  } else {
    throw response.json().then((errorJson) => {
      return { ...errorJson };
    });
  }
};

const _computeBasicTextResponse = (response: Response): Promise<any> => {
  if (response.ok || response.status === 401) {
    return response.text();
  } else if (response.status === 500) {
    throw response.text().then((errorText) => {
      return { errorText, traceId: response.headers.get('trace-id') };
    });
  } else {
    throw response.text();
  }
};

const _computeBasicResponseWithoutBody = (response: Response): Promise<any> => {
  if (response.ok) {
    return response.bodyUsed ? response.json() : Promise.resolve();
  }
  if (response.status === 401) {
    return response.json();
  } else if (response.status === 500) {
    throw response.json().then((errorJson) => {
      return { ...errorJson, traceId: response.headers.get('trace-id') };
    });
  } else {
    throw response.json().then((errorJson) => {
      return { ...errorJson };
    });
  }
};

const _computeAsyncBasicResponse = async (response: Response): Promise<any> => {
  if (response.ok || response.status === 401) {
    return response.json();
  } else if (response.status === 500) {
    throw await response.json().then((errorJson) => {
      return { ...errorJson, traceId: response.headers.get('trace-id') };
    });
  } else {
    throw await response.json().then((errorJson) => {
      return { ...errorJson };
    });
  }
};

const _computeDeleteResponse = (response: Response): Promise<boolean> => {
  if (response.status === 204) {
    return Promise.resolve(true);
  } else if (response.status === 401) {
    return response.json();
  } else if (response.status === 500) {
    throw response.json().then((errorJson) => {
      return { ...errorJson, traceId: response.headers.get('trace-id') };
    });
  } else {
    throw response.json().then((errorJson) => {
      return { ...errorJson };
    });
  }
};

// ---------------------------------------------------------------------------
// Still exported for tests / any existing direct usages
// ---------------------------------------------------------------------------

export const computeBasicResponse = _computeBasicResponse;
export const computeBasicTextResponse = _computeBasicTextResponse;
export const computeBasicResponseWithoutBody = _computeBasicResponseWithoutBody;
export const computeAsyncBasicResponse = _computeAsyncBasicResponse;
export const computeDeleteResponse = _computeDeleteResponse;

export const createQueryParameters = (params: any): string => {
  const urlParams = new URLSearchParams();
  for (const key in params) {
    const value = params[key];
    if (value !== undefined) {
      switch (typeof value) {
        case 'boolean':
          urlParams.set(key, value ? 'true' : 'false');
          break;
        case 'number':
          urlParams.set(key, `${value}`);
          break;
        default:
          urlParams.set(key, value);
      }
    }
  }
  return urlParams.toString();
};

export const fixedEncodeURIComponent = (filename: string): string => {
  return encodeURIComponent(filename).replace(/[!'()*]/g, function (c) {
    return '%' + c.charCodeAt(0).toString(16);
  });
};

// ---------------------------------------------------------------------------
// FetchClient — injectable HTTP client for all SGM Docs API calls
// ---------------------------------------------------------------------------

export type FetchClient = {
  readonly baseUrl: string;

  readonly authorizationHeader: string | null;

  get<T>(url: string, opts?: { signal?: AbortSignal }): Promise<T>;

  post<T>(url: string, body?: unknown): Promise<T>;

  patch<T>(url: string, body?: unknown): Promise<T>;

  put<T>(url: string, body?: unknown): Promise<T>;

  del(url: string): Promise<boolean>;

  deleteJson<T>(url: string): Promise<T>;

  getText(url: string, accept: string): Promise<string>;

  postVoid(url: string, body?: unknown, signal?: AbortSignal): Promise<void>;

  head(url: string, accept: string): Promise<number>;

  download(url: string, accept: string, range?: string): Promise<Response>;

  raw(url: string, opts?: { accept?: string; range?: string; omitAuth?: boolean }): Promise<Response>;

  upload<T>(url: string, body: BodyInit, extraHeaders: Record<string, string>): Promise<T>;
};

export const createFetchClient = (
  authorizationHeader: string | null,
  baseUrl: string,
  component: string = HEADER.COMPONENT_WEB
): FetchClient => {
  const jsonHeaders = (): Record<string, string> => ({
    authorization: authorizationHeader ?? '',
    'Content-Type': HEADER.APPLICATION_JSON,
    accept: HEADER.APPLICATION_JSON,
    'sgm-docs-component': component,
  });

  return {
    baseUrl,
    authorizationHeader,

    get<T>(url: string, opts?: { signal?: AbortSignal }): Promise<T> {
      return fetch(url, { headers: jsonHeaders(), signal: opts?.signal }).then(_computeBasicResponse);
    },

    post<T>(url: string, body?: unknown): Promise<T> {
      return fetch(url, {
        method: 'POST',
        headers: jsonHeaders(),
        body: body === undefined ? undefined : JSON.stringify(body),
      }).then(_computeAsyncBasicResponse);
    },

    patch<T>(url: string, body?: unknown): Promise<T> {
      return fetch(url, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: body === undefined ? undefined : JSON.stringify(body),
      }).then(_computeAsyncBasicResponse);
    },

    put<T>(url: string, body?: unknown): Promise<T> {
      return fetch(url, {
        method: 'PUT',
        headers: jsonHeaders(),
        body: body === undefined ? undefined : JSON.stringify(body),
      }).then(_computeAsyncBasicResponse);
    },

    del(url: string): Promise<boolean> {
      return fetch(url, {
        method: 'DELETE',
        headers: jsonHeaders(),
      }).then(_computeDeleteResponse);
    },

    deleteJson<T>(url: string): Promise<T> {
      return fetch(url, {
        method: 'DELETE',
        headers: jsonHeaders(),
      }).then(_computeAsyncBasicResponse);
    },

    getText(url: string, accept: string): Promise<string> {
      return fetch(url, {
        headers: {
          authorization: authorizationHeader ?? '',
          Accept: accept,
          'sgm-docs-component': component,
        },
      }).then(_computeBasicTextResponse);
    },

    postVoid(url: string, body?: unknown, signal?: AbortSignal): Promise<void> {
      return fetch(url, {
        method: 'POST',
        headers: jsonHeaders(),
        body: body === undefined ? undefined : JSON.stringify(body),
        signal,
      }).then(_computeBasicResponseWithoutBody);
    },

    head(url: string, accept: string): Promise<number> {
      return fetch(url, {
        method: 'HEAD',
        headers: {
          authorization: authorizationHeader ?? '',
          'Content-Type': HEADER.APPLICATION_JSON,
          accept,
          'sgm-docs-component': component,
        },
      }).then((r) => Number.parseInt(r.headers.get('Content-Length') || '0', 10));
    },

    download(url: string, accept: string, range?: string): Promise<Response> {
      const headers: Record<string, string> = {
        authorization: authorizationHeader ?? '',
        'Content-Type': HEADER.APPLICATION_JSON,
        accept,
        'sgm-docs-component': component,
      };
      if (range) {
        headers['range'] = range;
      }
      return fetch(url, { headers: headers });
    },

    raw(url: string, opts?: { accept?: string; range?: string; omitAuth?: boolean }): Promise<Response> {
      const headers: Record<string, string> = {
        'Content-Type': HEADER.APPLICATION_JSON,
        accept: opts?.accept ?? '*/*',
        'sgm-docs-component': component,
      };
      if (!opts?.omitAuth && authorizationHeader) {
        headers['authorization'] = authorizationHeader;
      }
      if (opts?.range) {
        headers['range'] = opts.range;
      }
      return fetch(url, { headers: headers });
    },

    upload<T>(url: string, body: BodyInit, extraHeaders: Record<string, string>): Promise<T> {
      return fetch(url, {
        method: 'POST',
        headers: {
          authorization: authorizationHeader ?? '',
          'sgm-docs-component': component,
          ...extraHeaders,
        },
        body,
      }).then(_computeAsyncBasicResponse);
    },
  };
};
